import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../components/context/darkModeContext";
import { catalogPlanService } from "../../utils/services/catalogPlanService";
import DashboardContainer from "../../components/dashboard-container";
import { clientService } from "../../utils/services/clientService";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { inputFieldSx } from "../../constants/fieldStyles";
import { toast } from "react-toastify";
import CurrencyTextField from "../../components/currency-field/CurrencyTextField";
import CatalogPlanList from "../../components/catalog-plan-list";

const CURRENT_YEAR = new Date().getFullYear();

export default function CatalogPlans() {
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const clientId = localStorage.getItem("client_id");

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // ── New Plan modal ─────────────────────────────────────────────────────
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newYear, setNewYear] = useState(CURRENT_YEAR);
  const [newStatus, setNewStatus] = useState("draft");
  const [newScenario, setNewScenario] = useState("start-of-year");
  const [newBudget, setNewBudget] = useState("");
  const [newDimension, setNewDimension] = useState("category");
  const [selectedClient, setSelectedClient] = useState(null);

  const textSecondary = darkMode ? "#aaa" : "#666";

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    const effectiveClientId = clientId || selectedClient?._id || null;
    try {
      const res = await catalogPlanService.fetch({
        client_id: effectiveClientId,
      });
      setPlans(res?.data || []);
    } catch (err) {
      console.error("CatalogPlans fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedClient]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleNewPlanSubmit = () => {
    if (!newName.trim()) return;
    const budgetRaw = parseFloat(newBudget);
    const effectiveClientId = clientId || selectedClient?._id || null;
    if (!effectiveClientId) {
      toast.error("Please select a client for the new plan.");
      return;
    }
    navigate("/catalog-planner", {
      state: {
        name: newName.trim(),
        year: newYear,
        status: newStatus,
        scenario: newScenario,
        dimension: newDimension,
        budget: !isNaN(budgetRaw) && budgetRaw > 0 ? budgetRaw * 1000 : null,
        client_id: effectiveClientId,
      },
    });
  };

  const filteredPlans = plans.filter((p) =>
    searchValue
      ? p.name?.toLowerCase().includes(searchValue.toLowerCase())
      : true,
  );

  return (
    <>
      <DashboardContainer
        header="Catalog Plans"
        title="+ New Plan"
        searchBarPlaceholder="Search by Plan Name"
        hideUploadButton
        hideFilterClient
        hideGridList
        onSearchData={(val) => setSearchValue(val)}
        onClickAddButton={() => {
          setNewName("");
          setNewYear(CURRENT_YEAR);
          setNewStatus("draft");
          setNewScenario("start-of-year");
          setNewBudget("");
          setNewDimension("category");
          setNewModalOpen(true);
        }}
        onClickRefresh={() => fetchPlans()}
      >
        <CatalogPlanList
          data={filteredPlans}
          loading={loading}
          clientId={clientId}
          onEditData={(plan) => navigate(`/catalog-planner/${plan._id}`)}
          onDeleteData={() => fetchPlans()}
        />
      </DashboardContainer>

      {/* ── New Plan Modal ── */}
      <Dialog
        open={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
          New Catalog Plan
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "16px !important",
          }}
        >
          {!clientId && (
            <AutocompleteWithPagination
              selectedItem={selectedClient}
              onDataChange={(val) => setSelectedClient(val)}
              width="100%"
              optionKey="business_name"
              backgroundColor="var(--bg-paper)"
              customLabel="Client"
              apiEndPoint={(limit, skip, search) =>
                clientService.fetch(limit, skip, search, null, "Active")
              }
            />
          )}
          <TextField
            label="Plan Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleNewPlanSubmit()}
            sx={inputFieldSx}
          />
          <FormControl size="small" fullWidth sx={inputFieldSx}>
            <InputLabel>Year</InputLabel>
            <Select
              value={newYear}
              label="Year"
              onChange={(e) => setNewYear(e.target.value)}
            >
              {[
                CURRENT_YEAR - 2,
                CURRENT_YEAR - 1,
                CURRENT_YEAR,
                CURRENT_YEAR + 1,
              ].map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={inputFieldSx}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="active">Active</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={inputFieldSx}>
            <InputLabel>Scenario</InputLabel>
            <Select
              value={newScenario}
              label="Scenario"
              onChange={(e) => setNewScenario(e.target.value)}
            >
              <MenuItem value="start-of-year">Start of Year</MenuItem>
              <MenuItem value="mid-year">Mid Year</MenuItem>
              <MenuItem value="end-of-year">End of Year</MenuItem>
              <MenuItem value="match-ly">Match Last Year</MenuItem>
            </Select>
          </FormControl>
          <FormControl
            size="small"
            fullWidth
            sx={{
              border: "1px solid var(--border-default)",
              borderRadius: 1,
              px: 1.5,
              pt: 0.5,
              pb: 0.25,
              position: "relative",
            }}
          >
            <FormLabel
              sx={{
                position: "absolute",
                top: -9,
                left: 10,
                px: 0.5,
                bgcolor: "var(--bg-paper)",
                fontSize: 12,
                lineHeight: 1,
                color: "var(--text-secondary)",
                "&.Mui-focused": { color: "var(--text-secondary)" },
              }}
            >
              Plan By
            </FormLabel>
            <RadioGroup
              row
              value={newDimension}
              onChange={(e) => setNewDimension(e.target.value)}
            >
              <FormControlLabel
                value="category"
                control={<Radio size="small" />}
                label="Category"
              />
              <FormControlLabel
                value="product_type"
                control={<Radio size="small" />}
                label="Product Type"
              />
            </RadioGroup>
          </FormControl>

          <CurrencyTextField
            label="Estimated Annual Budget ($K)"
            value={newBudget}
            onChange={(e) => setNewBudget(e)}
            size="small"
            placeholder="Optional — leave blank to use Last Year"
            inputProps={{ min: 0 }}
            sx={inputFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setNewModalOpen(false)}
            sx={{ color: textSecondary, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!newName.trim()}
            onClick={handleNewPlanSubmit}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
